import { useNavigate } from 'react-router-dom';

import { IcsBadge } from 'assets/icons/ics-badge';
import {
  LineChart,
  NumberType,
  RatingDiffTriangle,
  ValueWithPending,
  ZoneLogo,
  ZoneStatus,
  TableRowItem,
  Tooltip,
  NumberFormat,
} from 'components';
import { IbcVolumeDisclaimer } from 'components/IbcVolumeDisclaimer';
import { ICS_SECURED_CHAINS } from 'constants/ics-chains';
import { overviewPath } from 'routing';
import { IsGravityBridge } from 'services/IsGravityBridge';

import styles from './TableRow.module.scss';
import { TableRowProps, ZoneData } from './TableRow.props';
import { ColumnKeys } from '../Types';

const ratingDiffKeysMap: Record<ColumnKeys, keyof ZoneData> = {
  ibcVolumeReceived: 'ibcVolumeInRatingDiff',
  ibcVolumeSent: 'ibcVolumeOutRatingDiff',
  ibcVolume: 'ibcVolumeRatingDiff',
  ibcTransfers: 'ibcTransfersRatingDiff',
  totalTxs: 'totalIbcTxsRatingDiff',
  activeAddresses: 'dauRatingDiff',
  ibcDau: 'ibcDauRatingDiff',
};

export function TableRow({
  index,
  isTableHorizontalScrollable,
  selectedColumnKey,
  zone,
}: TableRowProps) {
  const navigate = useNavigate();

  const ratingDiff = zone[ratingDiffKeysMap[selectedColumnKey]] as number;

  const onClick = () => {
    navigate(`${zone.zone}/${overviewPath}`);
  };

  const showDisclaimer = IsGravityBridge(zone.zone);

  return (
    <tr className={styles.container} onClick={onClick}>
      <TableRowItem isSticky={isTableHorizontalScrollable}>
        <span className={styles.position}>{index + 1}</span>
      </TableRowItem>

      <TableRowItem isSticky={isTableHorizontalScrollable} withBorder={isTableHorizontalScrollable}>
        <div className={styles.zoneBaseInfoContainer}>
          <ZoneLogo logoUrl={zone.logoUrl} className={styles.logo} />
          <div className={styles.zoneNameContainer}>
            <span className={styles.zoneName}>{zone.name}</span>
            {(ICS_SECURED_CHAINS as string[]).includes(zone.zone) && (
              <Tooltip content="Interchain Security" body="Interchain Security">
                <IcsBadge />
              </Tooltip>
            )}
          </div>
          <ZoneStatus className={styles.status} status={zone.isZoneUpToDate} />
          <RatingDiffTriangle className={styles.ratingDiff} ratingDiff={ratingDiff} />
          {showDisclaimer && <IbcVolumeDisclaimer className={styles.disclaimer} />}
        </div>
      </TableRowItem>

      <TableRowItem>
        <ValueWithPending
          alignRight={true}
          className={styles.value}
          numberType={NumberType.Currency}
          pendingValue={zone.ibcVolumePending}
          value={zone.ibcVolume}
        />
      </TableRowItem>

      <TableRowItem>
        <ValueWithPending
          alignRight={true}
          className={styles.value}
          numberType={NumberType.Currency}
          pendingValue={zone.ibcVolumeInPending}
          value={zone.ibcVolumeIn}
        />
      </TableRowItem>

      <TableRowItem withBorder={true}>
        <ValueWithPending
          alignRight={true}
          className={styles.value}
          numberType={NumberType.Currency}
          pendingValue={zone.ibcVolumeOutPending}
          value={zone.ibcVolumeOut}
        />
      </TableRowItem>

      <TableRowItem>
        <ValueWithPending alignRight={true} className={styles.value} numberType={NumberType.Number}>
          {zone.zone === '1' ? (
            '-'
          ) : (
            <NumberFormat value={zone.totalTxs} numberType={NumberType.Number} />
          )}
        </ValueWithPending>
      </TableRowItem>

      <TableRowItem withBorder={true}>
        <ValueWithPending
          alignRight={true}
          className={styles.value}
          numberType={NumberType.Number}
          pendingValue={zone.ibcTransfersPending}
          value={zone.ibcTransfers}
        />
      </TableRowItem>

      <TableRowItem>
        <ValueWithPending
          alignRight={true}
          className={styles.value}
          numberType={NumberType.Number}
          value={zone.peersCount}
        />
      </TableRowItem>

      <TableRowItem withBorder={true}>
        <ValueWithPending
          alignRight={true}
          className={styles.value}
          numberType={NumberType.Number}
          value={zone.channelsCount}
        />
      </TableRowItem>

      <TableRowItem>
        <ValueWithPending
          alignRight={true}
          className={styles.value}
          numberType={NumberType.Number}
          value={zone.dau}
        />
      </TableRowItem>

      <TableRowItem withBorder={true}>
        <ValueWithPending
          alignRight={true}
          className={styles.value}
          numberType={NumberType.Number}
          value={zone.ibcDau}
        />
      </TableRowItem>

      <TableRowItem>
        {zone.ibcVolumeChart && <LineChart data={zone.ibcVolumeChart} dataKey="volume" />}
      </TableRowItem>
    </tr>
  );
}
